import React, { useState } from "react";
import { Box, Typography, Modal, IconButton, Grid } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

interface PhotoGalleryProps {
  photos: string[];
  sitterName: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, sitterName }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const displayPhotos = photos.slice(0, 3);
  const remainingCount = Math.max(0, photos.length - 3);

  const handlePhotoClick = (index: number) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const handlePrevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <>
      <Box sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          {displayPhotos.map((photo, index) => (
            <Grid size={{ xs: 12, sm: 4 }} key={index}>
              <Box
                onClick={() => handlePhotoClick(index)}
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "66.67%", // 2:3 aspect ratio
                  backgroundColor: "#E0E0E0",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={`${sitterName}'s photo ${index + 1}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#F5F5F5",
                    }}
                  >
                    <Typography color="textSecondary">No Photo</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          ))}

          {/* View All Photos Card */}
          {remainingCount > 0 && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Box
                onClick={() => handlePhotoClick(3)}
                sx={{
                  position: "relative",
                  width: "100%",
                  paddingTop: "66.67%",
                  backgroundColor: "rgba(23, 139, 142, 0.9)",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                    backgroundColor: "rgba(15, 97, 100, 0.9)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    +{remainingCount}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    View All
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Lightbox Modal */}
      <Modal
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            backgroundColor: "black",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous Button */}
          {photos.length > 1 && (
            <IconButton
              onClick={handlePrevPhoto}
              sx={{
                position: "absolute",
                left: 16,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
                zIndex: 2,
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>
          )}

          {/* Current Photo */}
          {photos[currentPhotoIndex] ? (
            <img
              src={photos[currentPhotoIndex]}
              alt={`${sitterName}'s photo ${currentPhotoIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography color="white">No Photo</Typography>
          )}

          {/* Next Button */}
          {photos.length > 1 && (
            <IconButton
              onClick={handleNextPhoto}
              sx={{
                position: "absolute",
                right: 16,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
                zIndex: 2,
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          )}

          {/* Photo Counter */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              px: 2,
              py: 1,
              borderRadius: 1,
            }}
          >
            <Typography>
              {currentPhotoIndex + 1} / {photos.length}
            </Typography>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default PhotoGallery;
